import type { CarState, Control, PhysicsConfig, RampData, TornadoData, Vec2 } from "../types";
import { add, clip, cloneVec, headingVector, length, scale, sub } from "./math";
import type { TrackRuntime } from "./track";

export interface StuntConfig {
  gravity: number;
  minimum_launch_speed: number;
  clean_landing_vertical_speed: number;
  landing_grip_seconds: number;
  loop_drive_fraction: number;
  tornado_duration_seconds: number;
  tornado_full_spin_speed: number;
}

export interface StuntEvent {
  kind: "takeoff" | "landing" | "tornado-entry" | "tornado-complete" | "tornado-misfire" | "obstacle-clear";
  stuntId: string;
  z: number;
  speed: number;
  verticalSpeed: number;
  airborneTime: number;
  jumpDistance: number;
}

export class StuntController {
  private jumpOrigin: Vec2 | null = null;

  constructor(private readonly config: StuntConfig, private readonly physics: PhysicsConfig) {}

  updateAirborne(state: CarState, control: Control, dt: number): { car: CarState; event: StuntEvent | null } {
    const car = { ...state, position: cloneVec(state.position) };
    car.heading += clip(control.steering, -1, 1) * this.physics.steering_rate * this.physics.air_steering_factor * dt;
    car.position = add(car.position, scale(headingVector(car.heading), car.speed * dt));
    car.z = state.z + state.verticalVelocity * dt - 0.5 * this.config.gravity * dt * dt;
    car.verticalVelocity = state.verticalVelocity - this.config.gravity * dt;
    car.airborneTime += dt;
    car.landingGripSeconds = Math.max(0, state.landingGripSeconds - dt);
    if (car.z > 0 || car.verticalVelocity >= 0) return { car, event: null };

    const impactSpeed = Math.abs(car.verticalVelocity);
    car.z = 0;
    car.verticalVelocity = 0;
    car.airborne = false;
    car.pitch = 0;
    const severity = Math.max(0, impactSpeed / this.config.clean_landing_vertical_speed - 1);
    const stability = Math.max(0.35, this.physics.landing_stability);
    const yaw = Math.min(0.24, severity * 0.09 / stability);
    car.heading += yaw * (Math.sin(car.heading * 1.7) >= 0 ? 1 : -1);
    const retention = this.physics.landing_speed_retention - Math.min(0.22, severity * 0.08);
    car.speed *= Math.max(0.45, retention);
    car.landingGripSeconds = this.config.landing_grip_seconds / stability;
    const jumpDistance = this.jumpOrigin ? length(sub(car.position, this.jumpOrigin)) : 0;
    const event: StuntEvent = {
      kind: "landing", stuntId: state.stuntSegmentId ?? "airborne", z: 0,
      speed: car.speed, verticalSpeed: -impactSpeed, airborneTime: car.airborneTime, jumpDistance,
    };
    car.stuntSegmentId = null;
    car.stuntProgress = 0;
    car.stuntSpeed = 0;
    this.jumpOrigin = null;
    return { car, event };
  }

  launch(state: CarState, ramp: RampData): { car: CarState; event: StuntEvent } {
    const car = { ...state, position: cloneVec(state.position) };
    car.z = Math.max(car.z, ramp.height);
    car.verticalVelocity = Math.abs(car.speed) * Math.sin(ramp.angle) * this.physics.jump_velocity_factor;
    car.airborne = true;
    car.airborneTime = 0;
    car.pitch = -ramp.angle;
    car.stuntSegmentId = ramp.id;
    this.jumpOrigin = cloneVec(car.position);
    return { car, event: { kind: "takeoff", stuntId: ramp.id, z: car.z, speed: car.speed, verticalSpeed: car.verticalVelocity, airborneTime: 0, jumpDistance: 0 } };
  }

  enterTornado(state: CarState, tornado: TornadoData): { car: CarState; event: StuntEvent } {
    const car = { ...state, position: cloneVec(state.position), stuntSegmentId: tornado.id, stuntProgress: 0, stuntSpeed: Math.abs(state.speed), z: 0, pitch: 0, airborne: false };
    return { car, event: { kind: "tornado-entry", stuntId: tornado.id, z: 0, speed: car.speed, verticalSpeed: 0, airborneTime: 0, jumpDistance: 0 } };
  }

  updateTornado(state: CarState, tornado: TornadoData, track: TrackRuntime, control: Control, dt: number): { car: CarState; event: StuntEvent | null } {
    void control;
    const car = { ...state, position: cloneVec(state.position) };
    let spinFraction = clip(state.stuntSpeed / this.config.tornado_full_spin_speed, 0, 1);
    if (spinFraction >= 0.995) spinFraction = 1;
    const targetAngle = Math.PI * 2 * spinFraction;
    const angularSpeed = targetAngle / this.config.tornado_duration_seconds;
    const nextAngle = Math.min(targetAngle, state.stuntProgress + angularSpeed * dt);
    const elapsedFraction = targetAngle <= 1e-9 ? 1 : clip(nextAngle / targetAngle, 0, 1);
    const groundProgress = tornado.start_progress + (tornado.end_progress - tornado.start_progress) * elapsedFraction;
    const pose = track.pointAt(groundProgress);
    car.position = pose.point;
    car.heading = pose.heading + nextAngle;
    car.stuntProgress = nextAngle;
    car.stuntSpeed = state.stuntSpeed;
    car.speed = state.stuntSpeed;
    car.z = 0; car.pitch = 0; car.verticalVelocity = 0; car.airborne = false;
    if (nextAngle + 1e-9 < targetAngle) return { car, event: null };
    const exit = track.pointAt(tornado.end_progress + 0.01);
    const headingOffset = ((targetAngle + Math.PI) % (Math.PI * 2)) - Math.PI;
    Object.assign(car, { position: exit.point, heading: exit.heading + headingOffset, stuntSegmentId: null, stuntProgress: 0, stuntSpeed: 0 });
    const kind = spinFraction === 1 ? "tornado-complete" : "tornado-misfire";
    return { car, event: { kind, stuntId: tornado.id, z: 0, speed: car.speed, verticalSpeed: 0, airborneTime: 0, jumpDistance: 0 } };
  }
}
